import React, { useState, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button as MuiButton,
  Paper,
  Divider,
} from '@mui/material'
import { Button } from '@loadsmart/loadsmart-ui'
import FileViewer from 'molecules/FileViewer/FileViewer'
import { TranslationTestCaseResult } from 'common/types/kraken-core/TranslationMap'
import { TranslationTestCaseDetail } from 'common/types/kraken-core/TranslationMap'

export interface NewTestCaseDialogProps {
  open: boolean
  onClose: () => void
  onCreate: (testCase: TranslationTestCaseDetail) => void
  isCreating?: boolean
}

const steps = ['Basic Info', 'Input File', 'Expected Output', 'Review & Create']

export default function NewTestCaseDialog({
  open,
  onClose,
  onCreate,
  isCreating = false,
}: NewTestCaseDialogProps) {
  const [activeStep, setActiveStep] = useState(0)
  const [formData, setFormData] = useState({
    description: '',
    expectedResult: TranslationTestCaseResult.SUCCESS,
    inputPayload: '',
    expectedPayload: '',
    inputFileName: '',
    expectedFileName: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)
  const expectedFileInputRef = useRef<HTMLInputElement>(null)

  const handleNext = () => {
    const currentErrors = validateCurrentStep()
    if (Object.keys(currentErrors).length === 0) {
      setActiveStep(prev => prev + 1)
      setErrors({})
    } else {
      setErrors(currentErrors)
    }
  }

  const handleBack = () => {
    setActiveStep(prev => prev - 1)
    setErrors({})
  }

  const handleReset = () => {
    setActiveStep(0)
    setFormData({
      description: '',
      expectedResult: TranslationTestCaseResult.SUCCESS,
      inputPayload: '',
      expectedPayload: '',
      inputFileName: '',
      expectedFileName: '',
    })
    setErrors({})
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const validateCurrentStep = (): Record<string, string> => {
    const newErrors: Record<string, string> = {}

    switch (activeStep) {
      case 0:
        if (!formData.description.trim()) {
          newErrors.description = 'Description is required'
        }
        break
      case 1:
        if (!formData.inputPayload.trim()) {
          newErrors.inputPayload = 'Input payload is required'
        }
        break
      case 2:
        if (!formData.expectedPayload.trim()) {
          newErrors.expectedPayload = 'Expected payload is required'
        }
        break
    }

    return newErrors
  }

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'input' | 'expected'
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = e => {
        const content = e.target?.result as string
        if (type === 'input') {
          setFormData(prev => ({
            ...prev,
            inputPayload: content,
            inputFileName: file.name,
          }))
        } else {
          setFormData(prev => ({
            ...prev,
            expectedPayload: content,
            expectedFileName: file.name,
          }))
        }
      }
      reader.readAsText(file)
    }
  }

  const handleCreate = () => {
    const finalErrors = validateCurrentStep()
    if (Object.keys(finalErrors).length > 0) {
      setErrors(finalErrors)
      return
    }

    const testCase: TranslationTestCaseDetail = {
      description: formData.description,
      input_definition: {
        payload: formData.inputPayload,
      },
      expectation_definition: {
        result: formData.expectedResult,
        payload: formData.expectedPayload,
        headers: {},
      },
    }

    onCreate(testCase)
  }

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Test Case Description"
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              error={!!errors.description}
              helperText={errors.description || 'Describe what this test case validates'}
              multiline
              rows={3}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Expected Result</InputLabel>
              <Select
                value={formData.expectedResult}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    expectedResult: e.target.value as TranslationTestCaseResult,
                  }))
                }
                label="Expected Result"
              >
                <MenuItem value={TranslationTestCaseResult.SUCCESS}>Success</MenuItem>
                <MenuItem value={TranslationTestCaseResult.FAILURE}>Failure</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )

      case 1:
        return (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload or paste the input file content that will be used to test your mapping rules.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                  Upload Input File
                </Button>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".json,.xml,.txt,.csv"
                onChange={e => handleFileUpload(e, 'input')}
              />
            </Box>
            {formData.inputFileName && (
              <Alert severity="success" sx={{ mb: 2 }}>
                File loaded: {formData.inputFileName}
              </Alert>
            )}
            <Box sx={{ minHeight: '300px' }}>
              <FileViewer
                content={formData.inputPayload}
                contentType="json"
                showOptions={false}
                options={{ heigth: 300 }}
                onChange={value => setFormData(prev => ({ ...prev, inputPayload: value || '' }))}
              />
            </Box>
            {errors.inputPayload && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errors.inputPayload}
              </Alert>
            )}
          </Box>
        )

      case 2:
        return (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload or paste the expected output file content that should be generated by your
              mapping rules.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Button variant="secondary" onClick={() => expectedFileInputRef.current?.click()}>
                  Upload Expected Output File
                </Button>
              </Box>
              <input
                ref={expectedFileInputRef}
                type="file"
                hidden
                accept=".json,.xml,.txt,.csv"
                onChange={e => handleFileUpload(e, 'expected')}
              />
            </Box>
            {formData.expectedFileName && (
              <Alert severity="success" sx={{ mb: 2 }}>
                File loaded: {formData.expectedFileName}
              </Alert>
            )}
            <Box sx={{ minHeight: '300px' }}>
              <FileViewer
                content={formData.expectedPayload}
                contentType="json"
                showOptions={false}
                options={{ heigth: 300 }}
                onChange={value => setFormData(prev => ({ ...prev, expectedPayload: value || '' }))}
              />
            </Box>
            {errors.expectedPayload && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errors.expectedPayload}
              </Alert>
            )}
          </Box>
        )

      case 3:
        return (
          <Box sx={{ pt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Review Test Case
            </Typography>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Description
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {formData.description}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Expected Result
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {formData.expectedResult}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Input File
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {formData.inputFileName || 'Manual input'}
              </Typography>
              <Box
                sx={{
                  maxHeight: '150px',
                  overflow: 'auto',
                  backgroundColor: 'grey.50',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                >
                  {formData.inputPayload.substring(0, 500)}
                  {formData.inputPayload.length > 500 && '...'}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Expected Output File
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {formData.expectedFileName || 'Manual input'}
              </Typography>
              <Box
                sx={{
                  maxHeight: '150px',
                  overflow: 'auto',
                  backgroundColor: 'grey.50',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                >
                  {formData.expectedPayload.substring(0, 500)}
                  {formData.expectedPayload.length > 500 && '...'}
                </Typography>
              </Box>
            </Paper>
          </Box>
        )

      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Test Case</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {renderStepContent(index)}
                <Box sx={{ mb: 2, mt: 2 }}>
                  <MuiButton
                    variant="contained"
                    onClick={index === steps.length - 1 ? handleCreate : handleNext}
                    disabled={isCreating}
                    sx={{ mr: 1 }}
                  >
                    {index === steps.length - 1 ? 'Create Test Case' : 'Continue'}
                  </MuiButton>
                  <MuiButton disabled={index === 0} onClick={handleBack}>
                    Back
                  </MuiButton>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}
